import {
  BatchRequestProperties,
  BatchIdSerializer,
  BatchRequestError,
  BatchItemProcessor,
  QueueItem,
  ItemProcessorResult
} from './batchRequestConstants';

import { getFailureCooldown } from './batchRequestUtil';

class BatchRequestProcessor {
  private completeItems: Map<string, object> = new Map();

  private requestQueue: Array<QueueItem> = [];

  private active = false;

  handleBatchResult(
    itemsProcessor: BatchItemProcessor,
    batch: Array<QueueItem>,
    error: BatchRequestError,
    properties: BatchRequestProperties
  ) {
    let minimumCooldown = 0;
    const currentDate = new Date().getTime();
    batch.forEach((request: QueueItem) => {
      if (this.completeItems.has(request.key)) {
        request.resolve(this.completeItems.get(request.key));
      } else if (
        properties.maxRetryAttempts &&
        properties.maxRetryAttempts > 0 &&
        error !== BatchRequestError.unretriableFailure
      ) {
        const itemCooldown = getFailureCooldown(request.retryAttempts, properties);

        if (minimumCooldown > 0) {
          minimumCooldown = Math.min(minimumCooldown, itemCooldown);
        } else {
          minimumCooldown = itemCooldown;
        }

        if (++request.retryAttempts <= properties.maxRetryAttempts) {
          request.queueAfter = currentDate + itemCooldown;
          // Put in front of the queue to make sure duplicate items
          // don't get processed without the cooldown time.
          this.requestQueue.unshift(request);
        } else {
          request.reject(BatchRequestError.maxAttemptsReached);
        }
      } else {
        request.reject(error);
      }
    });

    if (minimumCooldown > 0) {
      setTimeout(
        () => this.processQueue(itemsProcessor, properties),
        minimumCooldown + properties.processBatchWaitTime
      );
    }
    this.active = false;
    this.processQueue(itemsProcessor, properties);
  }

  processQueue(itemsProcessor: BatchItemProcessor, properties: BatchRequestProperties) {
    if (this.active) {
      return;
    }
    const batch: Array<QueueItem> = [];
    const batchKeys: Map<string, object> = new Map();
    const requeueRequests: Array<QueueItem> = [];
    const currentDate = new Date().getTime();

    while (batch.length < properties.batchSize && this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();

      if (request.queueAfter > currentDate) {
        batchKeys.set(request.key, request);
        requeueRequests.push(request);
        continue;
      } else if (this.completeItems.has(request.key)) {
        request.resolve(this.completeItems.get(request.key));
      } else if (batchKeys.has(request.key)) {
        // Requeue to make sure duplicate requests still get resolved once they're completed.
        requeueRequests.push(request);
      } else {
        batchKeys.set(request.key, request);
        batch.push(request);
      }
    }

    this.requestQueue.push(...requeueRequests);

    if (batch.length <= 0) {
      return;
    }

    this.active = true;

    itemsProcessor(batch).then(
      (data: ItemProcessorResult) => {
        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            this.saveCompleteItem(key, data[key], properties);
          }
        }

        this.handleBatchResult(itemsProcessor, batch, BatchRequestError.processFailure, properties);
      },
      error => {
        this.handleBatchResult(itemsProcessor, batch, error, properties);
      }
    );
  }

  saveCompleteItem(key: string, data: any, properties: BatchRequestProperties) {
    this.completeItems.set(key, data);
    if (properties.getItemExpiration) {
      setTimeout(() => {
        this.completeItems.delete(key);
      }, properties.getItemExpiration(key));
    }
  }

  queueItem(
    itemId: number,
    itemProcessor: BatchItemProcessor,
    itemSerializer: BatchIdSerializer,
    properties: BatchRequestProperties
  ) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({
        key: itemSerializer(itemId),
        itemId,
        retryAttempts: 0,
        queueAfter: 0,
        startTime: new Date(),
        resolve,
        reject
      });
      setTimeout(
        () => this.processQueue(itemProcessor, properties),
        properties.processBatchWaitTime
      );
    });
  }

  invalidateItem(itemId: number, itemSerializer: BatchIdSerializer) {
    this.completeItems.delete(itemSerializer(itemId));
  }
}

export default BatchRequestProcessor;
